import React, { useState, useEffect } from 'react';
import { IAppScreen, ScreenTypes, ScreenType, IAppScreenContent, ScreenContentUtils } from './MobileAppTypes';
import { toast } from 'react-toastify';
import Button from '../_Core/Button';
import Modal from '../Modal/Modal';
import { EasyFileInput } from '../Tools/ExpandableDiv';
import Icon, { IconType } from '../Icon/Icon';
import { createScreenEditor } from './ScreenEditors';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { UiValuePairs } from '../../utils/CommonTypes/UserTypes';
import { FormatDate } from '../../utils/Tools';

interface IProps {
  screen?: IAppScreen;
  show: boolean;
  onSave: (screen: IAppScreen, fileDic: Record<string, File>) => void;
  onClose: () => void;
  onRemove: (screen: IAppScreen) => void;
  availableScreens?: IAppScreen[];
  existingClientFiles: UiValuePairs[];
}

const MobileAppScreenEditor: React.FC<IProps> = (props: IProps) => {
  const [formData, setFormData] = useState<IAppScreen>({
    Title: '',
    MenuIconUrl: '',
    MenuTitle: '',
    ShowInMenu: true,
    ShowInLowMenu:false,
    OrderInMenu: 100,
    Type: ScreenTypes.SIMPLE_TEXT_PAGE,
    JsonContentDic: {},
    AccessRole: 'Public',
    IsSecure: false,
    SplashImageUseDefault: false,
    SplashImageUrl: '',
    BackgroundImageUseDefault: false,
    BackgroundImageUrl: '',
    DisplayStartAtUtc: '',
    DisplayEndAtUtc: '',
  });

  const [content, setContent] = useState<IAppScreenContent>(ScreenContentUtils.getDefaultContent(ScreenTypes.SIMPLE_TEXT_PAGE));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fileDic, setFileDic] = useState<Record<string, File>>({});
  const [contentValidationErrors, setContentValidationErrors] = useState<string[]>([]);

  // Access roles available for screens
  const accessRoles = [
    { value: 'Public', label: 'Public (Everyone)' },
    { value: 'Participant', label: 'Participants Only' },
    { value: 'Judge', label: 'Judges Only' },
    { value: 'Staff', label: 'Staff Only' },
    { value: 'Admin', label: 'Administrators Only' },
  ];

  useEffect(() => {
    if (props.screen) {
      setFormData({
        ...props.screen,
        // Stored as UTC; show in the datetime-local input using the browser's local time for editing.
        DisplayStartAtUtc: FormatDate(props.screen.DisplayStartAtUtc, 'input-date-time') ?? '',
        DisplayEndAtUtc: FormatDate(props.screen.DisplayEndAtUtc, 'input-date-time') ?? '',
      });

      // Deserialize JsonContent based on screen type
      const content: any = props.screen.JsonContentDic;
      if (content) {
        setContent(content);
      } else {
        // If deserialization fails, create default content
        setContent(ScreenContentUtils.getDefaultContent(props.screen.Type as ScreenType));
      }
    } else {
      // New screen - set defaults
      const defaultType = ScreenTypes.SIMPLE_TEXT_PAGE;
      const defaultContent = ScreenContentUtils.getDefaultContent(defaultType);
      const defaultScreen = {
        Title: '',
        MenuIconUrl: '',
        MenuTitle: '',
        ShowInMenu: true,
        ShowInLowMenu:false,
        OrderInMenu: 100,
        Type: defaultType,
        JsonContentDic: {},
        AccessRole: 'Public',
        IsSecure: false,
        SplashImageUseDefault: false,
        SplashImageUrl: '',
        BackgroundImageUseDefault: false,
        BackgroundImageUrl: '',
        DisplayStartAtUtc: '',
        DisplayEndAtUtc: '',
      };
      setFormData(defaultScreen);
      setContent(defaultContent);
    }
  }, [props.screen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.Title || formData.Title.trim() === '') {
      newErrors.Title = 'Screen title is required';
    }

    if (!formData.MenuTitle || formData.MenuTitle.trim() === '') {
      newErrors.MenuTitle = 'Menu title is required';
    }

    if (formData.DisplayStartAtUtc && formData.DisplayEndAtUtc && new Date(formData.DisplayEndAtUtc) <= new Date(formData.DisplayStartAtUtc)) {
      newErrors.DisplayEndAtUtc = 'Display end must be after display start';
    }

    // Content validation is now handled by individual editors
    if (contentValidationErrors.length > 0) {
      newErrors.Content = contentValidationErrors.join('; ');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix the validation errors before saving.');
      return;
    }

    // Content to Record<string, string>
    let dic = ScreenContentUtils.ToDic(content);

    const screenToSave: IAppScreen = {
      ...formData,
      JsonContentDic: dic,
      DisplayStartAtUtc: formData.DisplayStartAtUtc || undefined,
      DisplayEndAtUtc: formData.DisplayEndAtUtc || undefined,
    };

    props.onSave(screenToSave, fileDic);
  };

  const handleRemove = () => {
    if (props.screen) {
      if (window.confirm('Are you sure you want to remove this screen?')) {
        props.onRemove(props.screen);
      }
    }
  };

  const updateFormData = (field: keyof IAppScreen, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleTypeChange = (newType: ScreenType) => {
    updateFormData('Type', newType);
    const newContent = ScreenContentUtils.getDefaultContent(newType);
    setContent(newContent);
    setContentValidationErrors([]); // Clear validation errors when changing type
  };

  const handleContentChange = (newContent: IAppScreenContent) => {
    setContent(newContent);
    // Clear content errors when content changes
    if (errors.Content) {
      setErrors((prev) => ({ ...prev, Content: '' }));
    }
  };

  const handleFilesChange = (newFiles: Record<string, File>) => {
    setFileDic((prev) => ({ ...prev, ...newFiles }));
  };

  const handleValidationChange = (validationErrors: string[]) => {
    setContentValidationErrors(validationErrors);
  };

  const renderContentEditor = () => {
    return createScreenEditor(formData.Type as ScreenType, {
      content,
      errors,
      onContentChange: handleContentChange,
      onFilesChange: handleFilesChange,
      onValidationChange: handleValidationChange,
      availableScreens: props.availableScreens || [],
    });
  };

  if (!props.show) return null;

  return (
    <Modal setModalOpen={props.onClose} title={props.screen ? 'Edit Screen' : 'Add Screen'} size="xl">
      <div className="mobile-app-screen-editor">
        <Tabs>
          <TabList>
            <Tab>Basic</Tab>
            <Tab>Content</Tab>
          </TabList>
          <TabPanel>
            {/* Basic Screen Properties */}
            <div className="form-section">
              <h4>Screen Properties</h4>
              <div className="form-horizontal">
                <div className="form-group">
                  <label className="col-sm-3 control-label">Screen Type *</label>
                  <div className="col-sm-9">
                    <select className="form-control" value={formData.Type} onChange={(e) => handleTypeChange(e.target.value as ScreenType)}>
                      <option value={ScreenTypes.SIMPLE_TEXT_PAGE}>Simple Text Page</option>
                      <option value={ScreenTypes.FORMATTED_TEXT_PAGE}>Formatted Text Page</option>
                      <option value={ScreenTypes.FAQ_PAGE}>FAQ Page</option>
                      <option value={ScreenTypes.FILE_PAGE}>File Page (PDF/Image/Text/Video)</option>
                      <option value={ScreenTypes.PARENT_SCREEN}>Parent/Group Screen</option>
                      <option value={ScreenTypes.NOTIFICATION_SCREEN}>Notification Screen</option>
                      <option value={ScreenTypes.WEBVIEW_SCREEN}>Web View Screen</option>
                      <option value={ScreenTypes.WEBLINK_SCREEN}>Web Link Screen</option>
                      <option value={ScreenTypes.FMS_WEB_PAGE}>FMS Page</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Title *</label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className={`form-control ${errors.Title ? 'has-error' : ''}`}
                      value={formData.Title}
                      onChange={(e) => updateFormData('Title', e.target.value)}
                      placeholder="Screen title"
                    />
                    {errors.Title && <span className="text-danger">{errors.Title}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Menu Title *</label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className={`form-control ${errors.MenuTitle ? 'has-error' : ''}`}
                      value={formData.MenuTitle}
                      onChange={(e) => updateFormData('MenuTitle', e.target.value)}
                      placeholder="Title shown in app menu"
                    />
                    {errors.MenuTitle && <span className="text-danger">{errors.MenuTitle}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Menu Icon</label>
                  <div className="col-sm-9">
                    <EasyFileInput
                      htmlId="menu-icon-upload"
                      exts=".jpg,.jpeg,.png,.gif,.svg,.webp,.heic"
                      fileSize={200}
                      valueOptions={props.existingClientFiles}
                      value={formData.MenuIconUrl}
                      onSelect={(url: string) => updateFormData('MenuIconUrl', url)}
                      onSelectFile={(file: File) => {
                        let newFileDic = { ...fileDic };
                        newFileDic['menu_icon'] = file;
                        setFileDic(newFileDic);
                      }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Menu Order</label>
                  <div className="col-sm-9">
                    <input type="number" className="form-control" value={formData.OrderInMenu} onChange={(e) => updateFormData('OrderInMenu', parseInt(e.target.value) || 0)} min="0" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Access Role</label>
                  <div className="col-sm-9">
                    <select className="form-control" value={formData.AccessRole} onChange={(e) => updateFormData('AccessRole', e.target.value)}>
                      {accessRoles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Show in Menu</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.ShowInMenu} onChange={(e) => updateFormData('ShowInMenu', e.target.checked)} />
                    <span className="ml-2">Display this screen in the app navigation menu</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Show in Low Menu</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.ShowInLowMenu} onChange={(e) => updateFormData('ShowInLowMenu', e.target.checked)} />
                    <span className="ml-2">Display this screen in the app low navigation menu</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Secure Screen</label>
                  <div className="col-sm-9">
                    <input type="checkbox" checked={formData.IsSecure} onChange={(e) => updateFormData('IsSecure', e.target.checked)} />
                    <span className="ml-2">Require authentication to access this screen</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Display Window</label>
                  <div className="col-sm-9">
                    <div className="flex" style={{ gap: '15px', flexWrap: 'wrap' }}>
                      <div>
                        <label className="control-label" style={{ display: 'block' }}>
                          Start
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={formData.DisplayStartAtUtc || ''}
                          onChange={(e) => updateFormData('DisplayStartAtUtc', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="control-label" style={{ display: 'block' }}>
                          End
                        </label>
                        <input
                          type="datetime-local"
                          className="form-control"
                          value={formData.DisplayEndAtUtc || ''}
                          onChange={(e) => updateFormData('DisplayEndAtUtc', e.target.value)}
                        />
                      </div>
                    </div>
                    <small className="help-block text-muted">Leave both blank to always display this screen. Times are in the fair's local time zone.</small>
                    {errors.DisplayEndAtUtc && <span className="text-danger">{errors.DisplayEndAtUtc}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Splash Image</label>
                  <div className="col-sm-9">
                    <div style={{ marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={formData.SplashImageUseDefault || false}
                        onChange={(e) => updateFormData('SplashImageUseDefault', e.target.checked)}
                      />
                      <span className="ml-2">Use default splash image</span>
                    </div>
                    <EasyFileInput
                      htmlId="splash-image-upload"
                      exts=".jpg,.jpeg,.png,.gif,.svg,.webp,.heic"
                      valueOptions={props.existingClientFiles}
                      fileSize={200}
                      value={formData.SplashImageUrl || ''}
                      readOnly={formData.SplashImageUseDefault}
                      onSelect={(url: string) => !formData.SplashImageUseDefault && updateFormData('SplashImageUrl', url)}
                      onSelectFile={(file: File) => {
                        if (!formData.SplashImageUseDefault) {
                          let newFileDic = { ...fileDic };
                          newFileDic['splash_image'] = file;
                          setFileDic(newFileDic);
                        }
                      }}
                    />
                    {formData.SplashImageUseDefault && (
                      <small className="help-block text-muted">Using default splash image from app theme</small>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="col-sm-3 control-label">Background Image</label>
                  <div className="col-sm-9">
                    <div style={{ marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={formData.BackgroundImageUseDefault || false}
                        onChange={(e) => updateFormData('BackgroundImageUseDefault', e.target.checked)}
                      />
                      <span className="ml-2">Use default background image</span>
                    </div>
                    <EasyFileInput
                      htmlId="background-image-upload"
                      exts=".jpg,.jpeg,.png,.gif,.svg,.webp,.heic"
                      fileSize={200} 
                      valueOptions={props.existingClientFiles}
                      value={formData.BackgroundImageUrl || ''}
                      readOnly={formData.BackgroundImageUseDefault}
                      onSelect={(url: string) => !formData.BackgroundImageUseDefault && updateFormData('BackgroundImageUrl', url)}
                      onSelectFile={(file: File) => {
                        if (!formData.BackgroundImageUseDefault) {
                          let newFileDic = { ...fileDic };
                          newFileDic['background_image'] = file;
                          setFileDic(newFileDic);
                        }
                      }}
                    />
                    {formData.BackgroundImageUseDefault && (
                      <small className="help-block text-muted">Using default background image from app theme</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
          <TabPanel>
            {/* Content Editor - Now using modular components */}
            {renderContentEditor()}
          </TabPanel>
        </Tabs>
        {errors.Content && (
          <div className="alert alert-danger">
            <Icon type={IconType.warning} />
            <strong>Content validation errors:</strong>
            <div>
              <ul style={{ marginBottom: 0, marginTop: '5px' }}>
                {errors.Content.split('; ').map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="form-section">
          <div className="flex-between">
            <div>{props.screen && <Button type="remove" text="Remove Screen" onClick={handleRemove} />}</div>
            <div>
                          <Button type="close" text="Cancel" onClick={props.onClose} />
              <Button type="save" text="Save Screen" onClick={handleSave} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MobileAppScreenEditor;
